import { HttpClient, Logger, LogLevel } from '@openticket/lib-shop';
import urlJoin from 'url-join';

export interface BotProtConfig {
    httpClient?: HttpClient;
    logger: Logger;
    shopApiUrl: string;
    deferredShopApiUrl?: string;
    serviceUrl: string;
}

interface BotProtEventDetail {
    final: boolean;
    jwt?: string;
    next?: string;
    status: number;
}

const BOTPROT_EVENT_LISTENER = 'BotProt:ChallengeHandled';

export class BotProtClient {
    log: Logger;
    serviceUrl: string;
    shopApiUrl: string;
    deferredShopApiUrl?: string;
    data?: Record<string, unknown>;
    identifier?: string;
    httpClient?: HttpClient;

    onChallengeComplete: (verified: boolean, jwt?: string) => void;

    private elementToRenderIn?: HTMLElement;

    /**
     * Creates a new BotProtClient instance.
     * Set onChallengeComplete to be called when the challenge(s) are completed.
     * @param config The configuration for the bot prot client.
     */
    constructor(config: BotProtConfig) {
        this.httpClient = config.httpClient;
        this.log = config.logger;
        this.shopApiUrl = config.shopApiUrl;
        this.deferredShopApiUrl = config.deferredShopApiUrl;
        this.serviceUrl = config.serviceUrl;

        this.onChallengeComplete = () => {
            /* No-op */
        };
    }

    /**
     * Initializes the bot protection, returns false if protection for this shop is not enabled.
     * @param identifier An identifier to distinguish requests from each other.
     * @param data Could be any object to send to the botprot service
     */
    async init(
        identifier: string,
        data?: Record<string, unknown>
    ): Promise<boolean> {
        this.data = data;

        if (!identifier) {
            throw new Error('BotProt: identifier is required');
        }
        this.identifier = identifier;

        try {
            // Quick check to see if this identifier needs a botprot challenge
            const response = await fetch(
                `https://ev3b.s3.eu-west-1.amazonaws.com/botprot/${this.identifier}`,
                { method: 'HEAD' }
            );

            if (!response.ok) {
                this.log.create(
                    LogLevel.Warning,
                    ['botprot', 'init', 'head', 'failed'],
                    {
                        identifier: identifier,
                        ...this.data,
                    },
                    'BotProt init head call failed.'
                );
            }

            return response.ok;
        } catch (e) {
            this.log.create(
                LogLevel.Warning,
                ['botprot', 'init', 'fetch', 'failed'],
                {
                    identifier: identifier,
                    error: e,
                },
                'BotProt init fetch call failed.'
            );

            return false;
        }
    }

    /**
     * Renders the bot protection challenge in the given element.
     * @param elementToRenderIn The element to render the challenge in.
     * @param loaderHtml Optional HTML to render while the challenge is being fetched.
     */
    async render(
        elementToRenderIn: HTMLElement,
        loaderHtml = '<div class="loader"><h2>...</h2></div>'
    ): Promise<void> {
        document.addEventListener(BOTPROT_EVENT_LISTENER, this.handleChallenge);

        this.elementToRenderIn = elementToRenderIn;
        this.elementToRenderIn.innerHTML = loaderHtml;

        // Initial render -> Get HTML as response to register call
        await this.registerAndRender();
    }

    /**
     * Renders the given HTML inside a div with the challenge html and .
     * @param html
     * @private
     */
    private async renderChallenge(html: string): Promise<void> {
        if (!this.elementToRenderIn || !html) {
            console.error(
                'BotProt: elementToRenderIn not set or empty HTML received.'
            );

            this.log.create(
                LogLevel.Warning,
                ['botprot', 'render', 'failed'],
                {
                    identifier: this.identifier,
                    htmlSet: !!html,
                },
                'BotProt render failed. elementToRenderIn or html not set.'
            );

            return;
        }

        this.elementToRenderIn.innerHTML = '';

        const container = document.createElement('div');
        container.innerHTML = html;

        // Replace scripts with new (running) ones
        const scripts = container.querySelectorAll('script');
        scripts.forEach(oldScript => {
            const newScript = document.createElement('script');

            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            if (oldScript.parentNode) {
                oldScript.parentNode.replaceChild(newScript, oldScript);
            }
        });

        this.elementToRenderIn.appendChild(container);
    }

    /**
     * Registers the challenge at the BotProt service. Returns the challengeId that needs to load
     */
    private async registerAndRender(): Promise<void> {
        const url = this.buildRegisterUrl(
            urlJoin(this.serviceUrl, 'register', this.identifier || ''),
            this.data
        );

        let response: Response | undefined;
        let location: string | null;
        try {
            response = await this.fetchWithRetries(url);

            if (
                !response ||
                !response.ok ||
                !('headers' in response) ||
                !response.headers
            ) {
                throw new Error(
                    `BotProt register call failed. Invalid response: ${response?.status} ${response?.statusText}`
                );
            }
            location = await response.headers.get('location');

            if (!location) {
                throw new Error(
                    `BotProt register call failed. Invalid location header.`
                );
            }
        } catch (e) {
            this.log.create(
                LogLevel.Error,
                ['botprot', 'register', 'failed', 'response'],
                {
                    error: e,
                    url,
                    identifier: this.identifier,
                    status: response?.status,
                    statusText: response?.statusText,
                    ok: response?.ok,
                    headers: response?.headers,
                },
                'BotProt register call failed. Invalid response.'
            );

            return;
        }

        await this.getAndRenderNextChallenge(location);
    }

    // Arrow function for preserving 'this' context
    handleChallenge = async (event: Event) => {
        const customEvent = event as CustomEvent<BotProtEventDetail>;

        if (!customEvent || !customEvent.detail || !customEvent.detail.status) {
            console.error(
                'BotProt: handleChallenge called with invalid event.'
            );
            this.log.create(
                LogLevel.Warning,
                ['botprot', 'handleChallenge', 'invalid', 'event'],
                {
                    identifier: this.identifier,
                    event: customEvent,
                },
                'BotProt handleChallenge called with invalid event.'
            );
            return;
        }

        if (customEvent.detail.status >= 400) {
            // eslint-disable-next-line no-self-assign
            window.location.href = window.location.href;
            return;
        }

        if (!customEvent.detail.jwt) {
            console.error('BotProt: handleChallenge called with invalid jwt.');
            this.log.create(
                LogLevel.Warning,
                ['botprot', 'handleChallenge', 'invalid', 'jwt'],
                {
                    identifier: this.identifier,
                    event: customEvent,
                },
                'BotProt handleChallenge called with invalid jwt.'
            );
            return;
        }

        if (customEvent.detail.final) {
            this.finalizeAndCleanup(customEvent.detail);
        } else {
            if (!customEvent.detail.next) {
                this.log.create(
                    LogLevel.Warning,
                    ['botprot', 'handleChallenge', 'invalid', 'next'],
                    {
                        identifier: this.identifier,
                        event: customEvent,
                    },
                    'BotProt handleChallenge called with no  next challenge.'
                );
            } else {
                await this.getAndRenderNextChallenge(customEvent.detail.next);
            }
        }
    };

    private async getAndRenderNextChallenge(nextUrl: string): Promise<void> {
        const html = await this.getChallenge(nextUrl);

        if (!html) {
            this.log.create(
                LogLevel.Warning,
                ['botprot', 'render', 'failed'],
                {
                    identifier: this.identifier,
                },
                'BotProt render failed. Empty HTML returned.'
            );
            return;
        }

        await this.renderChallenge(html);
    }

    private finalizeAndCleanup(event: BotProtEventDetail): void {
        if (!event.jwt) {
            this.log.create(
                LogLevel.Error,
                ['botprot', 'finalize', 'missing', 'jwt'],
                {
                    identifier: this.identifier,
                    event,
                },
                'BotProt finalizeAndCleanup called with no JWT.'
            );
            console.error('BotProt: finalizeAndCleanup called with no JWT.');
            return;
        }

        // TODO: This should be somewhere else if we want this to be a library
        if (this.httpClient) {
            this.httpClient.interceptors.request.use(request => {
                const apiHost = new URL(this.shopApiUrl).hostname;
                const deferredApiHost = this.deferredShopApiUrl
                    ? new URL(this.deferredShopApiUrl).hostname
                    : '';
                const interceptedHost = new URL(request.url || '').hostname;

                if (
                    ((apiHost && interceptedHost === apiHost) ||
                        (deferredApiHost &&
                            interceptedHost === deferredApiHost)) &&
                    event.jwt
                ) {
                    request.headers.set('x-botprot-verification', event.jwt);
                }

                return request;
            });
        }

        document.removeEventListener(
            BOTPROT_EVENT_LISTENER,
            this.handleChallenge
        );

        this.onChallengeComplete(true, event.jwt);
    }

    /**
     * Fetches the supplied challengeId from the BotProt service. Returns the HTML to render the challenge.
     * @param url The url returned by the BotProt service when registering the challenge.
     * @returns The HTML to render the challenge.
     */
    private async getChallenge(url: string): Promise<string | undefined> {
        let response: Response | undefined;
        try {
            response = await this.fetchWithRetries(url);

            if (
                !response ||
                !response.ok ||
                !('headers' in response) ||
                !response.headers
            ) {
                throw new Error(
                    `Failed to fetch challenge: ${response?.status} ${response?.statusText}`
                );
            }
        } catch (e) {
            this.log.create(
                LogLevel.Error,
                ['botprot', 'challenge', 'loading', 'failed'],
                {
                    error: e,
                    identifier: this.identifier,
                    url,
                    status: response?.status,
                    statusText: response?.statusText,
                    ok: response?.ok,
                },
                `Failed to fetch challenge`
            );

            return;
        }

        try {
            const html = await response.text();
            if (!html) {
                throw new Error('BotProt: No HTML returned');
            }
            return html;
        } catch (e) {
            this.log.create(
                LogLevel.Error,
                ['botprot', 'challenge', 'read', 'failed'],
                {
                    identifier: this.identifier,
                    url,
                    status: response?.status,
                    statusText: response?.statusText,
                    ok: response?.ok,
                    error: e,
                },
                `Failed to read body`
            );

            throw new Error(
                `Failed to read body: ${response.status} ${response.statusText}`
            );
        }
    }

    private async fetchWithRetries(
        url: string,
        maxRetries = 5,
        delayMs = 10000
    ) {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const response = await fetch(url);

                // Fetch only rejects on network errors.
                // We throw manually if the status isn't 2xx so it triggers a retry.
                if (!response.ok) {
                    throw new Error(`HTTP error status: ${response.status}`);
                }

                // If successful, return the response immediately
                return response;
            } catch (error) {
                // If this was the last attempt, throw the error back to the caller
                if (attempt === maxRetries) {
                    throw new Error(
                        `Failed to fetch after ${maxRetries} attempts. Last error: ${error.message}`
                    );
                }

                // Wait for the specified delay before the next loop iteration
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }
    }

    private buildRegisterUrl(
        url: string,
        data?: Record<string, unknown>
    ): string {
        const u = new URL(url);

        if (data) {
            Object.entries(data).forEach(([key, value]) => {
                if (value === null || value === undefined) {
                    return;
                } else if (typeof value === 'object') {
                    u.searchParams.append(
                        key.toLowerCase(),
                        JSON.stringify(value)
                    );
                } else {
                    try {
                        u.searchParams.append(
                            key.toLowerCase(),
                            value.toString()
                        );
                    } catch (e) {
                        /* No-op */
                    }
                }
            });
        }

        return u.toString();
    }
}
